import type { CSSProperties } from 'react';
import { SUIT_NAME, SUIT_SYMBOL } from '../game/match';
import { cardSuit, rankLabel, type Card } from '../game/rang';

export type CardSize = 'xs' | 'sm' | 'md' | 'lg';

const DIM: Record<CardSize, { w: number; h: number; corner: number; pip: number }> = {
  xs: { w: 22, h: 31, corner: 0, pip: 0 },
  sm: { w: 46, h: 66, corner: 13, pip: 24 },
  md: { w: 56, h: 80, corner: 15, pip: 30 },
  lg: { w: 70, h: 100, corner: 18, pip: 38 },
};

export const cardSize = (size: CardSize) => DIM[size];

const RANK_NAME: Record<string, string> = { A: 'Ace', K: 'King', Q: 'Queen', J: 'Jack' };
export const cardName = (c: Card) => `${RANK_NAME[c[0]!] ?? rankLabel(c)} of ${SUIT_NAME[cardSuit(c)]}`;

interface Props {
  card: Card;
  size?: CardSize;
  className?: string;
  style?: CSSProperties;
  /** Faded, e.g. a card you can't follow suit with. */
  dim?: boolean;
  /** Glows gold: a Rang (trump) card. */
  trump?: boolean;
}

export function PlayingCard({ card, size = 'md', className = '', style, dim, trump }: Props) {
  const d = DIM[size];
  const suit = cardSuit(card);
  const red = suit === 'H' || suit === 'D';
  const color = red ? 'text-suit-red' : 'text-ink';
  return (
    <span
      role="img"
      aria-label={cardName(card)}
      className={`relative inline-block shrink-0 overflow-hidden rounded-[9px] select-none ${color} ${dim ? 'saturate-50' : ''} ${className}`}
      style={{
        width: d.w,
        height: d.h,
        background: 'linear-gradient(160deg, #fffdf7 0%, #f7f1e3 60%, #ece2c9 100%)',
        boxShadow: trump
          ? '0 0 0 1.5px #d4a84b, 0 0 14px rgb(236 212 143 / 0.75), 0 6px 14px -6px rgb(0 0 0 / 0.6)'
          : '0 0 0 1px rgb(28 25 23 / 0.12), 0 6px 14px -6px rgb(0 0 0 / 0.55)',
        ...style,
      }}
    >
      <span className="absolute top-1 left-1.5 flex flex-col items-center leading-none font-extrabold" style={{ fontSize: d.corner }} aria-hidden>
        <span className="tracking-tighter">{rankLabel(card)}</span>
        <span style={{ fontSize: d.corner * 0.85 }}>{SUIT_SYMBOL[suit]}</span>
      </span>
      <span className="absolute inset-0 grid place-items-center pt-2 pl-2" style={{ fontSize: d.pip }} aria-hidden>
        {SUIT_SYMBOL[suit]}
      </span>
      {/* Darkened rather than see-through, so an overlapping hand never shows the card beneath. */}
      {dim && <span className="absolute inset-0 bg-felt-950/45" aria-hidden />}
      {trump && (
        <span className="absolute right-1 bottom-0.5 text-[8px] font-extrabold tracking-wider text-gold uppercase" aria-hidden>
          Rang
        </span>
      )}
    </span>
  );
}

export function CardBack({ size = 'md', className = '', style }: { size?: CardSize; className?: string; style?: CSSProperties }) {
  const d = DIM[size];
  return (
    <span
      aria-hidden
      className={`relative inline-block shrink-0 overflow-hidden rounded-[9px] ${className}`}
      style={{
        width: d.w,
        height: d.h,
        background:
          'repeating-linear-gradient(45deg, rgb(255 255 255 / 0.07) 0 3px, transparent 3px 7px), linear-gradient(155deg, #6d4ed1 0%, #a2529c 55%, #c0527a 100%)',
        boxShadow: 'inset 0 0 0 2px rgb(236 212 143 / 0.85), inset 0 0 0 4px rgb(0 0 0 / 0.15), 0 5px 12px -6px rgb(0 0 0 / 0.7)',
        ...style,
      }}
    >
      {d.pip > 0 && (
        <span className="absolute inset-0 grid place-items-center text-gold-soft/80" style={{ fontSize: d.pip * 0.6 }}>
          ♠
        </span>
      )}
    </span>
  );
}
